<template>
  <div>
    <h1>{{ $t("desktopBetaPage.title") }}</h1>
    <p class="intro">{{ $t("desktopBetaPage.intro") }}</p>
    <p v-if="availability === 'checking'" class="status">{{ $t("desktopBetaPage.checking") }}</p>
    <p v-else-if="availability === 'closed'" class="status">{{ $t("desktopBetaPage.closed") }}</p>
    <div v-else class="actions">
      <button-default class="download-button" :disabled="downloading !== ''" @click="download('windows')">
        <span v-if="downloading === 'windows'" class="download-button-content">
          <icon-spinner class="download-button-spinner" />
          {{ $t("desktopBetaPage.downloading") }}
        </span>
        <span v-else>{{ $t("desktopBetaPage.windowsButton") }}</span>
      </button-default>
      <button-default class="download-button" :disabled="downloading !== ''" @click="download('mac')">
        <span v-if="downloading === 'mac'" class="download-button-content">
          <icon-spinner class="download-button-spinner" />
          {{ $t("desktopBetaPage.downloading") }}
        </span>
        <span v-else>{{ $t("desktopBetaPage.macButton") }}</span>
      </button-default>
    </div>
    <p v-if="error" class="error">{{ error }}</p>
    <transition name="windows-install-guide">
      <desktop-beta-windows-install-dialog
        v-if="showWindowsInstallGuide"
        @close="onCloseWindowsInstallGuide"
      />
    </transition>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import ButtonDefault from "@/components/atoms/ButtonDefault.vue";
import DesktopBetaWindowsInstallDialog from "@/components/organisms/DesktopBetaWindowsInstallDialog.vue";
import {
  DESKTOP_BETA_DOWNLOAD_CLOSED,
  downloadDesktopInstaller,
  getNowMs,
  isDesktopBetaDownloadOpen,
} from "@/lib/desktopBetaDownload";
import type { DesktopInstallerPlatform } from "@/lib/desktopBetaDownload";
import IconSpinner from "~icons/figma/Spinner";

type Availability = "checking" | "open" | "closed";

interface Data {
  availability: Availability;
  downloading: DesktopInstallerPlatform | "";
  error: string;
  showWindowsInstallGuide: boolean;
}

interface Methods {
  refreshAvailability(): Promise<void>;
  clearWindowsInstallGuideTimer(): void;
  scheduleWindowsInstallGuide(): void;
  onCloseWindowsInstallGuide(): void;
  download(platform: DesktopInstallerPlatform): Promise<void>;
}

type WindowsInstallGuideTimerHost = {
  _windowsInstallGuideTimer: ReturnType<typeof setTimeout> | null;
};

function windowsInstallGuideTimerHost(vm: object): WindowsInstallGuideTimerHost {
  return vm as WindowsInstallGuideTimerHost;
}

export default Vue.extend<Data, Methods, unknown, unknown>({
  name: "DesktopBetaDownload",
  components: { ButtonDefault, IconSpinner, DesktopBetaWindowsInstallDialog },
  data() {
    return {
      availability: "checking",
      downloading: "",
      error: "",
      showWindowsInstallGuide: false,
    };
  },
  created() {
    windowsInstallGuideTimerHost(this)._windowsInstallGuideTimer = null;
    this.refreshAvailability();
  },
  beforeDestroy() {
    this.clearWindowsInstallGuideTimer();
  },
  methods: {
    async refreshAvailability() {
      const nowMs = await getNowMs();
      this.availability = isDesktopBetaDownloadOpen(nowMs) ? "open" : "closed";
    },
    clearWindowsInstallGuideTimer() {
      const host = windowsInstallGuideTimerHost(this);
      if (host._windowsInstallGuideTimer === null) return;
      clearTimeout(host._windowsInstallGuideTimer);
      host._windowsInstallGuideTimer = null;
    },
    scheduleWindowsInstallGuide() {
      this.clearWindowsInstallGuideTimer();
      const host = windowsInstallGuideTimerHost(this);
      host._windowsInstallGuideTimer = setTimeout(() => {
        host._windowsInstallGuideTimer = null;
        this.downloading = "";
        if (this.availability === "open") {
          this.showWindowsInstallGuide = true;
        }
      }, 2000);
    },
    onCloseWindowsInstallGuide() {
      this.showWindowsInstallGuide = false;
    },
    async download(platform: DesktopInstallerPlatform) {
      if (this.downloading || this.availability !== "open") return;
      this.error = "";
      this.downloading = platform;
      await this.$nextTick();
      if (platform === "windows") {
        this.scheduleWindowsInstallGuide();
      }
      try {
        await downloadDesktopInstaller(platform);
      } catch (error) {
        const message = error instanceof Error ? error.message : "";
        if (message === DESKTOP_BETA_DOWNLOAD_CLOSED) {
          this.availability = "closed";
          this.clearWindowsInstallGuideTimer();
          this.showWindowsInstallGuide = false;
          this.downloading = "";
        } else {
          this.error = this.$t("desktopBetaPage.downloadFailed") as string;
          this.clearWindowsInstallGuideTimer();
          this.showWindowsInstallGuide = false;
          this.downloading = "";
        }
      } finally {
        if (platform !== "windows") {
          this.downloading = "";
        }
      }
    },
  },
});
</script>

<style lang="scss" scoped>
h1 {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
  line-height: 1.4;
  text-align: center;
  color: var(--color-text-normal);
  margin: 8px 0 24px;
}

.intro {
  color: var(--color-text-normal);
  margin: 0 0 32px;
  font-size: var(--font-size-md);
  line-height: 1.7;
  text-align: left;
}

.actions {
  display: flex;
  flex-direction: row;
  gap: 16px;
  margin: 0 0 8px;

  @include mq(sp) {
    flex-direction: column;
  }
}

.status {
  margin: 0 0 8px;
  color: var(--color-text-normal);
  font-size: var(--font-size-md);
  line-height: 1.7;
  text-align: left;
}

.download-button {
  margin: 0;
  flex: 1;
  width: auto;
  white-space: nowrap;
  border-radius: var(--radius-rounded);
  background-color: var(--color-primary);
  border-color: var(--color-primary);
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-on-fixed-bg);
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }

  @include mq(sp) {
    width: 100%;
    font-size: var(--font-size-sm);
  }
}

.download-button-content {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.download-button-spinner {
  font-size: var(--font-size-md);
  color: currentColor;
  animation: download-spinner-rotate 0.8s linear infinite;
}

.error {
  margin-top: 20px;
  color: var(--color-warning);
  font-size: var(--font-size-xs);
  text-align: center;
}

@keyframes download-spinner-rotate {
  to {
    transform: rotate(360deg);
  }
}
</style>

<style lang="scss">
.windows-install-guide-enter-active,
.windows-install-guide-leave-active {
  transition: opacity 0.3s ease;
}

.windows-install-guide-enter,
.windows-install-guide-leave-to {
  opacity: 0;
}
</style>
